import { NeumaticoResult } from '../../../../common/api/search'
import { EnumFilter } from '../../../../shared/catalogo/type/filter'

export type TemporadaCatalogo = 'verano' | 'allseason' | 'invierno'

const TEMPORADA = {
  VERANO: 'verano',
  ALLSEASON: 'allseason',
  INVIERNO: 'invierno',
} as const

const temporadaSet = new Set<TemporadaCatalogo>([
  TEMPORADA.VERANO,
  TEMPORADA.ALLSEASON,
  TEMPORADA.INVIERNO,
])

const TEMPORADA_PRIORITY: TemporadaCatalogo[] = [
  TEMPORADA.VERANO,
  TEMPORADA.ALLSEASON,
  TEMPORADA.INVIERNO,
]

const seasonActive = (value: boolean | number | null | undefined): boolean =>
  value === 1 || value === true

export function neumaticoMatchesTemporada(
  neumatico: Pick<NeumaticoResult, 'verano' | 'invierno' | 'allseason'>,
  temporada: TemporadaCatalogo
): boolean {
  if (temporada === TEMPORADA.VERANO) return seasonActive(neumatico.verano)
  if (temporada === TEMPORADA.INVIERNO) return seasonActive(neumatico.invierno)
  return seasonActive(neumatico.allseason)
}

export function filterProductsByTemporada<
  T extends Pick<NeumaticoResult, 'verano' | 'invierno' | 'allseason'>,
>(products: T[], temporada: TemporadaCatalogo): T[] {
  return products.filter((product) => neumaticoMatchesTemporada(product, temporada))
}

export function extractTemporadasFromProducts(
  products: Pick<NeumaticoResult, 'verano' | 'invierno' | 'allseason'>[]
): TemporadaCatalogo[] {
  const temporadas = new Set<TemporadaCatalogo>()

  for (const product of products) {
    if (seasonActive(product.verano)) temporadas.add(TEMPORADA.VERANO)
    if (seasonActive(product.invierno)) temporadas.add(TEMPORADA.INVIERNO)
    if (seasonActive(product.allseason)) temporadas.add(TEMPORADA.ALLSEASON)
  }

  return Array.from(temporadas)
}

export function extractTemporadasFromEstacionFacet(
  estacionFilter: EnumFilter | undefined
): TemporadaCatalogo[] {
  if (!estacionFilter?.values?.length) return []

  return estacionFilter.values
    .filter((value) => value.count > 0 && temporadaSet.has(String(value.id) as TemporadaCatalogo))
    .map((value) => String(value.id) as TemporadaCatalogo)
}

export function pickPreferredTemporada(
  available: TemporadaCatalogo[]
): TemporadaCatalogo | null {
  for (const temporada of TEMPORADA_PRIORITY) {
    if (available.includes(temporada)) return temporada
  }
  return null
}

export function resolveAvailableTemporadas(
  products: Pick<NeumaticoResult, 'verano' | 'invierno' | 'allseason'>[],
  estacionFilter: EnumFilter | undefined
): TemporadaCatalogo[] {
  const fromFacet = extractTemporadasFromEstacionFacet(estacionFilter)
  if (fromFacet.length > 0) return fromFacet
  return extractTemporadasFromProducts(products)
}

export function shouldAutoAdjustTemporada(
  selected: TemporadaCatalogo | null,
  available: TemporadaCatalogo[],
  products: Pick<NeumaticoResult, 'verano' | 'invierno' | 'allseason'>[]
): TemporadaCatalogo | null {
  if (available.length === 0) return null

  const preferred = pickPreferredTemporada(available)
  if (!preferred) return null

  if (!selected) return preferred

  const selectedInAvailable = available.includes(selected)
  const selectedInProducts =
    products.length > 0 &&
    products.some((product) => neumaticoMatchesTemporada(product, selected))

  if (selectedInAvailable && (products.length === 0 || selectedInProducts)) {
    return null
  }

  return preferred === selected ? null : preferred
}
